import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { api, HsaAccount, CreateHsaAccountRequest, UpdateHsaAccountRequest, FamilyMember, CreateFamilyMemberRequest, UpdateFamilyMemberRequest } from '../api/client'
import { Analytics } from '@/analytics/events'
import { AccountsPageSkeleton } from '@/components/Skeleton/AccountsPageSkeleton'

type AccountOwner = 'Self' | 'Spouse'
type AccountStatus = 'Active' | 'Inactive' | 'Closed' | 'RolledOver'

const STATUS_LABELS: Record<AccountStatus, string> = {
  Active: 'Active',
  Inactive: 'Inactive',
  Closed: 'Closed',
  RolledOver: 'Rolled Over',
}

const STATUS_COLORS: Record<AccountStatus, string> = {
  Active: 'bg-green-100 text-green-800',
  Inactive: 'bg-gray-100 text-gray-800',
  Closed: 'bg-red-100 text-red-800',
  RolledOver: 'bg-blue-100 text-blue-800',
}

interface AccountFormData {
  name: string
  custodian: string
  accountNumber: string
  owner: AccountOwner
  status: AccountStatus
  balance: string
  notes: string
}

const emptyForm: AccountFormData = {
  name: '',
  custodian: '',
  accountNumber: '',
  owner: 'Self',
  status: 'Active',
  balance: '',
  notes: '',
}

// Family Member types
type RelationshipType = 'Self' | 'Spouse' | 'Child' | 'Parent' | 'Other'

const RELATIONSHIP_LABELS: Record<RelationshipType, string> = {
  Self: 'Self',
  Spouse: 'Spouse',
  Child: 'Child',
  Parent: 'Parent',
  Other: 'Other',
}

const RELATIONSHIP_COLORS: Record<RelationshipType, string> = {
  Self: 'bg-blue-100 text-blue-800',
  Spouse: 'bg-purple-100 text-purple-800',
  Child: 'bg-green-100 text-green-800',
  Parent: 'bg-orange-100 text-orange-800',
  Other: 'bg-gray-100 text-gray-800',
}

interface MemberFormData {
  name: string
  relationship: RelationshipType
  dateOfBirth: string
  isDependent: boolean
}

const emptyMemberForm: MemberFormData = {
  name: '',
  relationship: 'Self',
  dateOfBirth: '',
  isDependent: true,
}

export default function AccountsPage() {
  const [accounts, setAccounts] = useState<HsaAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState<HsaAccount | null>(null)
  const [formData, setFormData] = useState<AccountFormData>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  // Family Members state
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([])
  const [membersLoading, setMembersLoading] = useState(true)
  const [showMemberModal, setShowMemberModal] = useState(false)
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null)
  const [memberFormData, setMemberFormData] = useState<MemberFormData>(emptyMemberForm)
  const [savingMember, setSavingMember] = useState(false)
  const [deleteMemberConfirm, setDeleteMemberConfirm] = useState<string | null>(null)

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true)
      const data = await api.getHsaAccounts()
      if (data) setAccounts(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  const openAddModal = () => {
    setEditingAccount(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (account: HsaAccount) => {
    setEditingAccount(account)
    setFormData({
      name: account.name,
      custodian: account.custodian || '',
      accountNumber: account.accountNumber || '',
      owner: account.owner,
      status: account.status,
      balance: account.balance?.toString() || '',
      notes: account.notes || '',
    })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingAccount(null)
    setFormData(emptyForm)
  }

  const handleSave = async () => {
    if (!formData.name.trim()) return

    setSaving(true)
    try {
      if (editingAccount) {
        const update: UpdateHsaAccountRequest = {
          name: formData.name,
          custodian: formData.custodian || undefined,
          accountNumber: formData.accountNumber || undefined,
          owner: formData.owner,
          status: formData.status,
          balance: formData.balance ? parseFloat(formData.balance) : undefined,
          notes: formData.notes || undefined,
        }
        await api.updateHsaAccount(editingAccount.id, update)
      } else {
        const create: CreateHsaAccountRequest = {
          name: formData.name,
          custodian: formData.custodian || undefined,
          accountNumber: formData.accountNumber || undefined,
          owner: formData.owner,
          status: formData.status,
          balance: formData.balance ? parseFloat(formData.balance) : undefined,
          notes: formData.notes || undefined,
        }
        await api.createHsaAccount(create)
        Analytics.accountCreated()
      }
      closeModal()
      fetchAccounts()
      toast.success(editingAccount ? 'Account updated' : 'Account added')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save account')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await api.deleteHsaAccount(id)
      setDeleteConfirm(null)
      fetchAccounts()
      toast.success('Account deleted')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete account')
      setDeleteConfirm(null)
    }
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)

  // Family Members functions
  const fetchFamilyMembers = useCallback(async () => {
    try {
      setMembersLoading(true)
      const data = await api.getFamilyMembers()
      if (data) setFamilyMembers(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load family members')
    } finally {
      setMembersLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFamilyMembers()
  }, [fetchFamilyMembers])

  const openAddMemberModal = () => {
    setEditingMember(null)
    setMemberFormData(emptyMemberForm)
    setShowMemberModal(true)
  }

  const openEditMemberModal = (member: FamilyMember) => {
    setEditingMember(member)
    setMemberFormData({
      name: member.name,
      relationship: member.relationship,
      dateOfBirth: member.dateOfBirth || '',
      isDependent: member.isDependent,
    })
    setShowMemberModal(true)
  }

  const closeMemberModal = () => {
    setShowMemberModal(false)
    setEditingMember(null)
    setMemberFormData(emptyMemberForm)
  }

  const handleSaveMember = async () => {
    if (!memberFormData.name.trim()) return

    setSavingMember(true)
    try {
      if (editingMember) {
        const update: UpdateFamilyMemberRequest = {
          name: memberFormData.name,
          relationship: memberFormData.relationship,
          dateOfBirth: memberFormData.dateOfBirth || undefined,
          isDependent: memberFormData.isDependent,
        }
        await api.updateFamilyMember(editingMember.id, update)
      } else {
        const create: CreateFamilyMemberRequest = {
          name: memberFormData.name,
          relationship: memberFormData.relationship,
          dateOfBirth: memberFormData.dateOfBirth || undefined,
          isDependent: memberFormData.isDependent,
        }
        await api.createFamilyMember(create)
      }
      closeMemberModal()
      fetchFamilyMembers()
      toast.success(editingMember ? 'Family member updated' : 'Family member added')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save family member')
    } finally {
      setSavingMember(false)
    }
  }

  const handleDeleteMember = async (id: string) => {
    try {
      await api.deleteFamilyMember(id)
      setDeleteMemberConfirm(null)
      fetchFamilyMembers()
      toast.success('Family member deleted')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete family member')
      setDeleteMemberConfirm(null)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HSA Accounts</h1>
          <p className="text-gray-600 mt-1">
            Manage your HSA accounts and family members.
          </p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          + Add Account
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Show skeleton on initial load when both sections are loading */}
      {loading && membersLoading ? (
        <AccountsPageSkeleton />
      ) : (
        <>
          {/* HSA Accounts */}
          <div className="card mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Your HSA Accounts</h2>

            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading accounts...</div>
            ) : accounts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No HSA accounts added yet.</p>
            <p className="text-sm mt-1">Add your HSA accounts to track reimbursements by source.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-900">{account.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[account.status]}`}>
                        {STATUS_LABELS[account.status]}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        {account.owner}
                      </span>
                    </div>

                    {account.custodian && (
                      <p className="text-sm text-gray-600 mt-1">
                        Custodian: {account.custodian}
                        {account.accountNumber && <span> &middot; Acct: {account.accountNumber}</span>}
                      </p>
                    )}

                    <div className="flex gap-6 mt-3 text-sm">
                      {account.balance != null && (
                        <div>
                          <span className="text-gray-500">Balance:</span>{' '}
                          <span className="font-medium">{formatCurrency(account.balance)}</span>
                          {account.balanceAsOf && (
                            <span className="text-gray-400 text-xs ml-1">
                              as of {new Date(account.balanceAsOf).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Reimbursements:</span>{' '}
                        <span className="font-medium">{account.reimbursementCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Reimbursed:</span>{' '}
                        <span className="font-medium">{formatCurrency(account.totalReimbursed)}</span>
                      </div>
                    </div>

                    {account.notes && (
                      <p className="text-sm text-gray-500 mt-2">{account.notes}</p>
                    )}
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      className="text-sm text-navy-900 hover:underline"
                      onClick={() => openEditModal(account)}
                    >
                      Edit
                    </button>
                    {deleteConfirm === account.id ? (
                      <div className="flex gap-1">
                        <button
                          className="text-sm text-red-600 hover:underline"
                          onClick={() => handleDelete(account.id)}
                        >
                          Confirm
                        </button>
                        <button
                          className="text-sm text-gray-500 hover:underline"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="text-sm text-red-600 hover:underline"
                        onClick={() => setDeleteConfirm(account.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Family Members */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Family Members</h2>
          <button className="btn-secondary text-sm" onClick={openAddMemberModal}>
            + Add Member
          </button>
        </div>

        {membersLoading ? (
          <div className="text-center py-8 text-gray-500">Loading family members...</div>
        ) : familyMembers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No family members added yet.</p>
            <p className="text-sm mt-1">Add family members to tag expenses by person.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {familyMembers.map((member) => (
              <div
                key={member.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-900">{member.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RELATIONSHIP_COLORS[member.relationship]}`}>
                        {RELATIONSHIP_LABELS[member.relationship]}
                      </span>
                      {member.isDependent && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Dependent
                        </span>
                      )}
                    </div>

                    <div className="flex gap-6 mt-3 text-sm">
                      {member.dateOfBirth && (
                        <div>
                          <span className="text-gray-500">DOB:</span>{' '}
                          <span className="font-medium">{new Date(member.dateOfBirth + 'T00:00:00').toLocaleDateString()}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Expenses:</span>{' '}
                        <span className="font-medium">{member.expenseCount}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      className="text-sm text-navy-900 hover:underline"
                      onClick={() => openEditMemberModal(member)}
                    >
                      Edit
                    </button>
                    {deleteMemberConfirm === member.id ? (
                      <div className="flex gap-1">
                        <button
                          className="text-sm text-red-600 hover:underline"
                          onClick={() => handleDeleteMember(member.id)}
                        >
                          Confirm
                        </button>
                        <button
                          className="text-sm text-gray-500 hover:underline"
                          onClick={() => setDeleteMemberConfirm(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="text-sm text-red-600 hover:underline"
                        onClick={() => setDeleteMemberConfirm(member.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
          </div>
        </>
      )}

      {/* Add/Edit Account Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingAccount ? 'Edit HSA Account' : 'Add HSA Account'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="label">Account Name *</label>
                  <input
                    className="input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder='e.g., "Fidelity - Current Employer"'
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Custodian</label>
                    <input
                      className="input"
                      value={formData.custodian}
                      onChange={(e) => setFormData({ ...formData, custodian: e.target.value })}
                      placeholder="e.g., Fidelity"
                    />
                  </div>
                  <div>
                    <label className="label">Account Number</label>
                    <input
                      className="input"
                      value={formData.accountNumber}
                      onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Owner</label>
                    <select
                      className="input"
                      value={formData.owner}
                      onChange={(e) => setFormData({ ...formData, owner: e.target.value as AccountOwner })}
                    >
                      <option value="Self">Self</option>
                      <option value="Spouse">Spouse</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Status</label>
                    <select
                      className="input"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as AccountStatus })}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Closed">Closed</option>
                      <option value="RolledOver">Rolled Over</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="label">Balance</label>
                  <input
                    className="input"
                    type="number"
                    step="0.01"
                    value={formData.balance}
                    onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                    placeholder="Current balance (optional)"
                  />
                </div>

                <div>
                  <label className="label">Notes</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes about this account"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={saving || !formData.name.trim()}
                >
                  {saving ? 'Saving...' : editingAccount ? 'Update Account' : 'Add Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Family Member Modal */}
      {showMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingMember ? 'Edit Family Member' : 'Add Family Member'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="label">Name *</label>
                  <input
                    className="input"
                    value={memberFormData.name}
                    onChange={(e) => setMemberFormData({ ...memberFormData, name: e.target.value })}
                    placeholder='e.g., "John Smith"'
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Relationship</label>
                    <select
                      className="input"
                      value={memberFormData.relationship}
                      onChange={(e) => setMemberFormData({ ...memberFormData, relationship: e.target.value as RelationshipType })}
                    >
                      <option value="Self">Self</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Child">Child</option>
                      <option value="Parent">Parent</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Date of Birth</label>
                    <input
                      className="input"
                      type="date"
                      value={memberFormData.dateOfBirth}
                      onChange={(e) => setMemberFormData({ ...memberFormData, dateOfBirth: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={memberFormData.isDependent}
                      onChange={(e) => setMemberFormData({ ...memberFormData, isDependent: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-navy-900 focus:ring-navy-900"
                    />
                    <span className="text-sm text-gray-700">Is a dependent (for HSA eligibility purposes)</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button className="btn-secondary" onClick={closeMemberModal}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSaveMember}
                  disabled={savingMember || !memberFormData.name.trim()}
                >
                  {savingMember ? 'Saving...' : editingMember ? 'Update Member' : 'Add Member'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
